import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * ChronosTracker's OWN auth shell — deliberately NOT the shared `AuthLayout`
 * (split / hero / aside / focus + glass `AuthCard`) that every sibling clone
 * inherited and which therefore read as the same template.
 *
 * Where the generic kit framed auth as "a glass card centered in a split brand
 * panel", ChronosTracker's identity is a **single stopwatch stage**: one
 * centered column whose chrome is a *tracked-time ribbon* — a stopwatch chip +
 * wordmark on top, the screen headline, and a thin **timeline ruler** (ticks on
 * an axis) standing in for the usual card border. The form pours full-bleed onto
 * the ambient background below the ruler — no glass surface, no two-column split.
 *
 *   ┌──────────────────────────────┐
 *   │  ◷ ChronosTracker  · eyebrow ·│  ← brand band: stopwatch chip + wordmark
 *   │  Big headline.               │
 *   │  Supporting line.            │
 *   │  ┬──┬───┬──┬────┬──┬───┬──┬─  │  ← timeline ruler (ticks), not a card edge
 *   │  email   [____________]      │  ← form FULL-BLEED on the ambient bg
 *   │  pass    [____________]      │     (no GlassSurface frame)
 *   │  [ Sign in ]                 │
 *   └──────────────────────────────┘
 *
 * So two products opened side by side do not look like the same shell, even
 * though they share primitives (Button, TextField, SocialAuthButtons,
 * disabled-until-ready). The scene is agnostic about the *form*: each screen
 * pours its fields in as `children`.
 *
 * ── SCROLL CONTRACT (#13, same fix as the kit / melodimals #4) ──────────────
 * `AppBackground` is `position: fixed; inset: 0; overflow: hidden` and its slot
 * is `h-full`, so anything rendered INSIDE it gets clipped with no way to
 * scroll. Here it is used as a pure decorative backdrop **sibling**, and the
 * content lives in its own scroll container.
 *
 * CLONE SHELL PECULIARITY: in ChronosTracker `global.css` pins `html, body {
 * overflow: hidden; height: 100% }` (so only inner panels ever scroll, never
 * the body). A `min-h-[100dvh]` content container would therefore GROW past the
 * viewport and get clipped by the body — the scroll would be dead. So the
 * scroll container is capped to the viewport (`h-[100dvh] overflow-y-auto`) and
 * **scrolls internally**; the `min-h-full` inner column centers when the form
 * fits and grows past the container (triggering its own scroll) when it
 * doesn't. So a tall form (register) or a short landscape viewport is always
 * fully reachable.
 */

interface AuthSceneProps {
  /** Eyebrow chip (e.g. "Welcome back"). Optional. */
  eyebrow?: string;
  /** Page headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The scene headline IS the page h1 for
   * ChronosTracker's auth (there is no card title). Pass "h2" only if the screen
   * owns its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Width of the column. "narrow" for login/forgot/reset, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered full-bleed below the brand ribbon. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function AuthScene(props: AuthSceneProps) {
  const Heading = (headingProps: { children: JSX.Element }) =>
    (props.headingLevel ?? "h1") === "h2" ? (
      <h2 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h2>
    ) : (
      <h1 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h1>
    );

  return (
    <>
      {/* Decorative fixed backdrop. NEVER the parent that scrolls. */}
      <AppBackground />

      {/* Real scroll container: capped to the viewport (the global
          `body { overflow: hidden }` would clip a taller one) so it scrolls
          INTERNALLY when the form does not fit (portrait or short landscape). */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-5 sm:px-6"
        style={{
          "padding-top": "max(2rem, env(safe-area-inset-top))",
          "padding-bottom": "max(2rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1.25rem, env(safe-area-inset-left))",
          "padding-right": "max(1.25rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-8"
          classList={{
            "max-w-sm": (props.width ?? "narrow") === "narrow",
            "max-w-md": props.width === "wide",
          }}
        >
          {/* Brand band — stopwatch chip + wordmark + per-screen eyebrow chip. */}
          <header class="flex flex-col gap-5">
            <div class="flex items-center justify-between gap-3">
              <Link
                to="/"
                class="group flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="ChronosTracker"
              >
                <span class="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95">
                  <svg
                    class="h-5 w-5"
                    viewBox="0 0 32 32"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    {/* stopwatch crown + button */}
                    <rect x="13.25" y="2" width="5.5" height="2.4" rx="1.1" fill="currentColor" />
                    <line x1="16" y1="4.4" x2="16" y2="6.2" stroke-width="2.4" />
                    {/* dial */}
                    <circle cx="16" cy="18.5" r="10.5" stroke-width="2.3" />
                    {/* running hand */}
                    <path d="M16 18.5 L16 11.5" stroke-width="2.6" />
                    <path d="M16 18.5 L20.5 21" stroke-width="2.6" />
                  </svg>
                </span>
                <span class="text-lg font-bold tracking-tight text-foreground">ChronosTracker</span>
              </Link>
              <Show when={props.eyebrow}>
                <span class="rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent ring-1 ring-accent/20">
                  {props.eyebrow}
                </span>
              </Show>
            </div>

            <Show when={props.headline}>
              <div class="space-y-2">
                <Heading>{props.headline}</Heading>
                <Show when={props.tagline}>
                  <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                    {props.tagline}
                  </p>
                </Show>
              </div>
            </Show>
          </header>

          {/* Timeline ruler instead of a card border — a tracked-time ribbon. */}
          <TimelineRule />

          {/* Form lives full-bleed on the ambient background (no glass card). */}
          <div class="w-full">{props.children}</div>

          <Show when={props.footer}>
            <footer class="mt-7 space-y-3 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}

/**
 * A thin "timeline" rule: a hairline axis with evenly spaced tick marks,
 * echoing ChronosTracker's tracked-time motif. Replaces the plain hairline a
 * generic shell would use, so the chrome itself signals "time". Purely
 * decorative (`aria-hidden`) and drawn with theme tokens (`currentColor` over
 * `text-border`), so it stays legible under any theme.
 */
function TimelineRule() {
  return (
    <div class="my-7 text-border" aria-hidden="true">
      <svg class="h-3 w-full" viewBox="0 0 100 6" preserveAspectRatio="none" fill="none">
        <line x1="0" y1="3" x2="100" y2="3" stroke="currentColor" stroke-width="0.5" />
        <g stroke="currentColor" stroke-width="0.6" stroke-linecap="round">
          <line x1="6" y1="1" x2="6" y2="5" />
          <line x1="20" y1="0.5" x2="20" y2="5.5" />
          <line x1="34" y1="1" x2="34" y2="5" />
          <line x1="50" y1="0.5" x2="50" y2="5.5" />
          <line x1="64" y1="1" x2="64" y2="5" />
          <line x1="80" y1="0.5" x2="80" y2="5.5" />
          <line x1="94" y1="1" x2="94" y2="5" />
        </g>
      </svg>
    </div>
  );
}
